import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CannotAddInteractionStepReason,
  GraphLink,
  GraphNode,
  JourneyMapGraphicProps,
} from './types';
import {
  buildInteractionSessionColumn,
  buildJourneyMap,
  calculateXMultiple,
  generateScaleFunction,
  interactionDirectionOffset,
  scaleLinks,
} from './util';
import { groupBy, isEmpty, mapValues, maxBy, noop } from 'lodash';
import { BOX_HEIGHT, BOX_WIDTH, CONTAINER_HEIGHT, MIN_INTERACTIONS } from './const';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { JourneyMapHeader } from './Header';
import css from './index.module.scss';
import { Box, DeprecatedIcon, Flex, InlineLink, Pane, Text } from '@fullstory/components';
import { useInView } from 'react-intersection-observer';
import { Nodes } from './Nodes';
import { Links } from './Links';
import { useNotifications } from '@fullstory/react-hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOtherEventsModal } from '../../hooks/useOtherEventsModal';
import { ElementToImage } from '@fullstory/library-ui';

const NODE_WIDTH = 307;
const PATH_WIDTH = 226;
const STEP_BUTTON_WIDTH = 250;
const VIZ_PADDING = 32;

const JourneyMapGraphic: React.FC<JourneyMapGraphicProps> = ({
  canAppendInteraction,
  customPivotId,
  customVars,
  pivotDep,
  deleteFilter,
  direction,
  filters,
  getGroupByPropsDisabled,
  getNamedElement,
  getNamedElementTooltip,
  getDefinedEvent,
  getDefinedEventTooltip,
  getApiEventTooltip,
  getPageDefTooltip,
  getPageName,
  actionCallbacks,
  inSameSession,
  journeyMap,
  journeysContainerRef,
  mouseInteractions,
  clickPivotId,
  seenPivotId,
  numInteractionsIndex,
  pagePivotId,
  pageVars,
  resetFilters = noop,
  resetOnBackgroundClick = false,
  scrollToRight,
  scrollToSankey,
  setFilters,
  setScrollToRight,
  setScrollToSankey,
  setVisibilityState,
  width,
  viewMetric,
  elemVars,
  isLinkInIntelligentPath = () => false,
  scrollTo,
}) => {
  const { journeyMapGraph, sessionCountsByDepth } = useMemo(() => {
    try {
      const journeyMapGraph = buildJourneyMap(journeyMap, direction);
      const sessionCountsByDepth = journeyMapGraph?.nodes.reduce(
        (acc: { [key: number]: number }, { depth, count }) => {
          acc[depth] ? (acc[depth] += count) : (acc[depth] = count);
          return acc;
        },
        {},
      );

      return { journeyMapGraph, sessionCountsByDepth };
    } catch (_) {
      return {};
    }
  }, [journeyMap, direction]);

  const otherEventsModal = useOtherEventsModal({
    pivotOthers: journeyMap?.pivotOthers,
    identValuesByHash: journeyMap?.identValuesByHash,
    getPageNameById: getPageName,
    getNamedElement,
    getDefinedEvent,
    sessionCountsByDepth,
    pageVars,
    customVars,
    elemVars,
  });
  const showOtherEventsForDepth = (depth: number) => {
    otherEventsModal.setOtherEventsModalDepth(depth);
    otherEventsModal.setShowOtherEventsModal(true);
  };

  const maxDepth = maxBy(journeyMapGraph?.nodes, n => n.depth)?.depth ?? 0;

  const selectedGraph = useMemo(() => {
    let shouldBotherWithFilters = false;
    if (filters && !isEmpty(filters)) {
      if (Object.keys(filters).length > 1) {
        shouldBotherWithFilters = true;
      } else if (filters[0]?.hash !== 'any') {
        shouldBotherWithFilters = true;
      }
    }
    if (shouldBotherWithFilters) {
      return buildJourneyMap(journeyMap, direction, filters, maxDepth + 1);
    }
    return null;
  }, [journeyMap, direction, filters, maxDepth]);
  const [inViewRef, inView] = useInView();
  const notifications = useNotifications();
  const intl = useIntl();
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollToRight) {
      const nodeRect = nodeRef.current?.getBoundingClientRect();
      if (nodeRect && journeysContainerRef) {
        scrollTo({
          behavior: 'smooth',
          top: nodeRect.y,
        });
        if (direction === JourneyMapSettings.Direction.DIRECTION_FORWARD) {
          journeysContainerRef.current?.scrollTo({
            behavior: 'smooth',
            left: nodeRect.x,
          });
        }
      }
      setScrollToRight(false);
    } else if (scrollToSankey && journeysContainerRef && journeyMapGraph?.nodes.length) {
      // scroll to sankey if triggered from explore logic and if there are results in graph
      const nodeRect = journeysContainerRef.current?.getBoundingClientRect();
      scrollTo({ behavior: 'smooth', top: nodeRect?.y });
      setScrollToSankey(false);
    }
  }, [
    direction,
    scrollTo,
    journeyMapGraph?.nodes.length,
    journeysContainerRef,
    scrollToRight,
    scrollToSankey,
    setScrollToRight,
    setScrollToSankey,
  ]);

  const graph = selectedGraph ?? journeyMapGraph;
  const selectedNodes = useMemo(() => {
    const nodes: { [key: string]: GraphNode } = {};
    graph?.nodes.forEach(n => (nodes[n.getId()] = n));
    return nodes;
  }, [graph?.nodes]);

  const selectedLinks = useMemo(() => {
    const links: { [key: string]: GraphLink } = {};
    graph?.links.forEach(l => (links[l.getId()] = l));
    return links;
  }, [graph?.links]);

  // prevent notifications from being spammed while scrolling happens
  const [notified, setNotified] = useState<boolean>(false);

  useEffect(() => {
    let reason;
    if (numInteractionsIndex > maxDepth) {
      reason = CannotAddInteractionStepReason.NO_DATA;
      if (!notified) {
        setNotified(true);
        // notify that the button is now disabled
        notifications.info(
          intl.formatMessage({
            defaultMessage: 'This journey has no data beyond this point.',
            id: 'Ffyb8i',
          }),
          'journey-no-data',
        );
      }
    } else if (inView) {
      reason = CannotAddInteractionStepReason.OTHER;
    }
    setVisibilityState(!inView, reason);
  }, [inView, maxDepth, numInteractionsIndex, notified, setVisibilityState, notifications, intl]);

  const isNodeInPath = useCallback(
    (item: GraphNode) => {
      // no filters means don't dim anything
      if (!filters || Object.keys(filters).length === 0) return true;

      const selectedNode = selectedNodes[item.getId()];
      return !!selectedNode;
    },
    [filters, selectedNodes],
  );

  const isLinkInPath = useCallback(
    (item: GraphLink) => {
      // no filters means don't dim anything
      if (!filters || Object.keys(filters).length === 0) return true;

      const selectedLink = selectedLinks[item.getId()];
      return !!selectedLink;
    },
    [filters, selectedLinks],
  );

  if (!journeyMapGraph?.nodes.length)
    return (
      <Pane backgroundColor="secondary" width="100%" height={CONTAINER_HEIGHT}>
        <Box paddingTop={64}>
          <Text appearance="heading2" align="center">
            <FormattedMessage defaultMessage="No results found" id="hX5PAb" />
          </Text>
        </Box>
        <Box paddingTop={24}>
          <Text appearance="bodyLarge" align="center">
            <FormattedMessage
              defaultMessage="<a>Check your settings</a> or try again with a different time frame."
              id="ZgOZmV"
              values={{
                a(text) {
                  return (
                    <InlineLink
                      href="https://help.fullstory.com/hc/en-us/articles/4410019422743"
                      newTab
                    >
                      {text}
                    </InlineLink>
                  );
                },
              }}
            />
          </Text>
        </Box>
      </Pane>
    );

  const interactionSessionColumn = buildInteractionSessionColumn(
    journeyMapGraph.nodes,
    interactionDirectionOffset(direction, canAppendInteraction),
  );
  const selectedInteractionSessionColumn = buildInteractionSessionColumn(
    graph!.nodes,
    interactionDirectionOffset(direction, canAppendInteraction),
  );
  const totalInteractions = Object.keys(interactionSessionColumn).length;
  const totalSourceCount =
    direction === JourneyMapSettings.Direction.DIRECTION_FORWARD
      ? interactionSessionColumn[0]?.[0]?.total
      : interactionSessionColumn[totalInteractions - 1]?.[0]?.total;
  // we want to scale the links with respect to other links in the same depth
  const scaleLinkFunction = generateScaleFunction(totalSourceCount ?? 0);
  const scaledLinks = scaleLinks(journeyMapGraph.links, scaleLinkFunction);

  const isForwardDirection = direction === JourneyMapSettings.Direction.DIRECTION_FORWARD;

  // entry dropins are at the top, so keep track of how much we need to offset the links
  const dropinOffsets: { [identAtDepth: string]: number } = {};
  const incomingFlowIndicatorHeights = mapValues(
    groupBy(scaledLinks, l => l.target.getId()),
    (l: GraphLink[]) => l.reduce((sum, link) => sum + link.height, 0),
  );

  journeyMapGraph.nodes.forEach(n => {
    const count = n.count;
    const nextCount = n.nextCount;
    let dropOff = scaleLinkFunction(count) - scaleLinkFunction(nextCount);
    // give minimum height to the indicator
    if (dropOff > 0) {
      dropOff = Math.max(dropOff, scaleLinkFunction(0));
      if (!isForwardDirection) {
        const incomingFlow = incomingFlowIndicatorHeights[n.getId()];
        // make sure the dropin doesn't overflow the node height
        dropinOffsets[n.getId()] = Math.min(dropOff, BOX_HEIGHT - (incomingFlow ?? 0));
      }
    }
  });

  const height = CONTAINER_HEIGHT;
  let minWidth = width;
  /**
   * Chart is setup node -> node -> node which means we calculate the width
   * by multiplying the nodes by depth count plus one (total nodes) and then
   * adding the path width (depth). Lastly we add padding.
   */
  const nodeWidth = (maxDepth + 1) * NODE_WIDTH;
  const pathWidth = maxDepth * PATH_WIDTH;
  if (nodeWidth + pathWidth + VIZ_PADDING > minWidth) {
    minWidth = nodeWidth + pathWidth + VIZ_PADDING;
  }

  // we only need it when we can append / show add step button
  if (canAppendInteraction) {
    minWidth += STEP_BUTTON_WIDTH;
  }
  const allSessions: React.ReactElement[] = [];

  for (let i = 0; i < totalInteractions; i++) {
    const selectedColumn = selectedInteractionSessionColumn[i];
    if (!selectedColumn) continue;
    const session = selectedColumn[0];
    const repositionedColumn = interactionSessionColumn[i];
    if (session) {
      if (repositionedColumn) {
        session.x = repositionedColumn[0]?.x ?? 0;
      }
      allSessions.push(
        <JourneyMapHeader
          key={`header-${i}`}
          interactionSession={session}
          index={i + 1}
          direction={direction}
          inSameSession={inSameSession}
          totalInteractions={totalInteractions}
          maxDepth={maxDepth}
        />,
      );
    }
  }
  let addStepInteractions = mouseInteractions;
  if (numInteractionsIndex !== maxDepth) {
    addStepInteractions = { onClick: noop };
  }
  const addColumnInteraction = (
    <>
      <foreignObject
        x={calculateXMultiple(isForwardDirection ? totalInteractions - 0.4 : 0)}
        y={0}
        width={BOX_WIDTH}
        height={100}
        className={numInteractionsIndex === maxDepth ? css.active : css.inactive}
      >
        <ElementToImage.Exclude>
          <Flex
            column
            justifyContent="flex-start"
            ref={numInteractionsIndex + 1 > MIN_INTERACTIONS ? nodeRef : null}
            testId="journeys-add-step"
            {...addStepInteractions}
          >
            <Box marginTop={16} ref={inViewRef}>
              <DeprecatedIcon size={40} name="add-fill" />
            </Box>
            <Box marginTop={8}>
              <Text
                appearance="bodySmallBold"
                color={numInteractionsIndex === maxDepth ? 'cta' : 'secondary'}
              >
                <FormattedMessage defaultMessage="ADD STEP" id="QAFFOJ" />
              </Text>
            </Box>
          </Flex>
        </ElementToImage.Exclude>
      </foreignObject>
    </>
  );

  return (
    <>
      <svg width={minWidth} height={height} viewBox={`0 0 ${minWidth} ${height}`}>
        <defs>
          <filter id="journeyMapNodeFilter">
            <feDropShadow floodOpacity={0.2} floodColor="currentcolor" />
          </filter>
        </defs>
        <rect
          width="100%"
          height={height}
          className={css.visualizationBackground}
          onClick={() => {
            if (resetOnBackgroundClick) {
              resetFilters();
            }
          }}
        />
        {allSessions}
        <Nodes
          canAppendInteraction={canAppendInteraction}
          customPivotId={customPivotId}
          customVars={customVars}
          pivotDep={pivotDep}
          deleteFilter={deleteFilter}
          direction={direction}
          filters={filters}
          getGroupByPropsDisabled={getGroupByPropsDisabled}
          getNamedElement={getNamedElement}
          getDefinedEvent={getDefinedEvent}
          getPageDefTooltip={getPageDefTooltip}
          getNamedElementTooltip={getNamedElementTooltip}
          getDefinedEventTooltip={getDefinedEventTooltip}
          getApiEventTooltip={getApiEventTooltip}
          getPageName={getPageName}
          actionCallbacks={actionCallbacks}
          identValuesByHash={journeyMap.identValuesByHash}
          isInPath={(n: GraphNode) => isNodeInPath(n)}
          links={scaledLinks}
          maxDepth={maxDepth}
          clickPivotId={clickPivotId}
          seenPivotId={seenPivotId}
          nodes={journeyMapGraph.nodes}
          pagePivotId={pagePivotId}
          pageVars={pageVars}
          scaleFunction={scaleLinkFunction}
          selectedNodes={selectedNodes}
          setFilters={setFilters}
          viewMetric={viewMetric}
          showOtherEventsForDepth={showOtherEventsForDepth}
          elemVars={elemVars}
        />
        <Links
          links={scaledLinks}
          direction={direction}
          canAppendInteraction={canAppendInteraction}
          isInPath={isLinkInPath}
          isInIntelligentPath={isLinkInIntelligentPath}
          dropinOffsets={dropinOffsets}
          inSameSession={inSameSession}
          selectedLinks={selectedLinks}
          maxDepth={maxDepth}
        />
        {canAppendInteraction && addColumnInteraction}
      </svg>
      {otherEventsModal.ModalComponent}
    </>
  );
};

const MemoizedJourneyMap = React.memo(JourneyMapGraphic);
MemoizedJourneyMap.displayName = 'JourneyMap';

export { MemoizedJourneyMap as JourneyMapGraphic };
